// Amounts as people type and read them (user request 2026-10-16): «همه فیلدهای قیمت توی همون فیلد ورود اطلاعات ۳
// رقم ۳ رقم جدا بشن و زیرشون به حروف بنویسه». One place for the grouping and the words, used by every price field.

const DIGIT = /[0-9۰-۹٠-٩]/
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'

/** The longest amount a field takes: 10¹³ rials is far above any price or charge. */
export const MAX_AMOUNT_DIGITS = 13

/** The digits of a text in Latin, whatever digits it was typed in; separators and anything else dropped. */
export const amountDigits = (text?: string | number | null): string =>
  String(text ?? '')
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))
    .replace(/[^0-9]/g, '')

/** Latin digits as a field shows them: Persian digits grouped by three («۱۰٬۰۰۰٬۰۰۰»). */
export const groupAmount = (digits: string): string =>
  digits ? Number(digits).toLocaleString('fa-IR') : ''

/**
 * What an amount field shows after a keystroke: the digits grouped by three as they are typed (Persian, Arabic or
 * Latin), and where the caret goes so it stays before the same digit.
 */
export const regroupAmount = (text: string, caret: number): {text: string; caret: number} => {
  const after = (text.slice(caret).match(new RegExp(DIGIT.source, 'g')) || []).length
  const digits = amountDigits((text.match(new RegExp(DIGIT.source, 'g')) || []).join(''))
    .replace(/^0+(?=\d)/, '')
    .slice(0, MAX_AMOUNT_DIGITS)
  if (!digits) return {text: '', caret: 0}

  const formatted = groupAmount(digits)
  if (after <= 0) return {text: formatted, caret: formatted.length}
  let count = 0
  for (let position = formatted.length - 1; position >= 0; position--) {
    if (DIGIT.test(formatted[position])) count++
    if (count === after) return {text: formatted, caret: position}
  }
  return {text: formatted, caret: 0}
}

const ONES = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه']
const TEENS = [
  'ده',
  'یازده',
  'دوازده',
  'سیزده',
  'چهارده',
  'پانزده',
  'شانزده',
  'هفده',
  'هجده',
  'نوزده',
]
const TENS = ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود']
const HUNDREDS = ['', 'صد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد']
// As amounts are said in Iran: a thousand billion, not a trillion.
const SCALES = ['', 'هزار', 'میلیون', 'میلیارد', 'هزار میلیارد', 'میلیون میلیارد']

const belowThousand = (value: number): string => {
  const parts: string[] = []
  const hundreds = Math.floor(value / 100)
  const rest = value % 100
  if (hundreds) parts.push(HUNDREDS[hundreds])
  if (rest >= 20) {
    parts.push(TENS[Math.floor(rest / 10)])
    if (rest % 10) parts.push(ONES[rest % 10])
  } else if (rest >= 10) {
    parts.push(TEENS[rest - 10])
  } else if (rest) {
    parts.push(ONES[rest])
  }
  return parts.join(' و ')
}

/** «یک میلیون و دویست و پنجاه هزار»: a whole amount in Persian words; empty for nothing, a fraction or too much. */
export const amountInWords = (value?: number | string | null): string => {
  const digits =
    typeof value === 'number'
      ? Number.isFinite(value)
        ? String(Math.trunc(value))
        : ''
      : amountDigits(value)
  if (!digits) return ''
  const amount = Number(digits)
  if (!Number.isSafeInteger(amount) || amount < 0) return ''
  if (amount === 0) return 'صفر'

  const groups: string[] = []
  let rest = amount
  for (let scale = 0; rest > 0 && scale < SCALES.length; scale++) {
    const group = rest % 1000
    if (group) {
      // «هزار», not «یک هزار»; «یک میلیون» as it is said.
      const words =
        scale === 1 && group === 1
          ? 'هزار'
          : [belowThousand(group), SCALES[scale]].filter(Boolean).join(' ')
      groups.unshift(words)
    }
    rest = Math.floor(rest / 1000)
  }
  return rest > 0 ? '' : groups.join(' و ')
}
